import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SimpleTable from 'components/shared/table/SimpleTable';
import { ROLES, TYPES, usersUtil } from 'services/utils/users-util';
import { WorkOrderStatusKeys } from 'services/utils/work-order-status-enum';
import CheckBox from 'components/shared/checkbox/CheckBox';
import { QuotesStatusKeys } from 'domain/quotes-status-enum';
import { TaskStatusKeys } from 'domain/task-status-enum';
import Number from 'components/shared/text/Number';
import SimpleTaskStatus from 'components/shared/table/cell-addons/SimpleTaskStatus';
import { simpleScrollToTop } from 'services/utils/scroller-util';

export default class TasksSimpleTable extends Component {
  static propTypes = {
    currentUser: PropTypes.shape({}).isRequired,
    currentWorkOrder: PropTypes.shape({}).isRequired,
    quotes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    selectAllChecked: PropTypes.bool.isRequired,
    selectedTasks: PropTypes.shape({}).isRequired,
    tasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    trades: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    changeOrderTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    onDeselectAllTasks: PropTypes.func.isRequired,
    onGoToTask: PropTypes.func.isRequired,
    onLoadTrades: PropTypes.func.isRequired,
    onSelectAllTasks: PropTypes.func.isRequired,
    onSelectTask: PropTypes.func.isRequired,
    onSelectTaskRequest: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    const { currentUser } = this.props;
    this.userIsContractor = usersUtil.hasType(currentUser, TYPES.CONTRACTOR);
    this.userIsAdmin =
      usersUtil.isAdmin(currentUser) ||
      usersUtil.hasType(currentUser, TYPES.GENERAL_CONTRACTOR);
    this.userIsSpackleAdmin =
      usersUtil.hasRole(currentUser, ROLES.SPACKLE_ADMIN) ||
      usersUtil.hasType(currentUser, TYPES.GENERAL_CONTRACTOR);
  }

  handleGenerateLink = (id) => {
    const { tasks } = this.props;
    const task = tasks[id];
    return `${task.work_order_id}/task/${task.id}`;
  };

  handleSelectAllCheck = () => {
    this.props.onSelectAllTasks();
  };

  handleTaskCheckSelection = (taskId) => {
    this.props.onSelectTask(taskId);
  };

  handleTaskSelection = (index) => {
    simpleScrollToTop();

    const {
      currentUser,
      currentWorkOrder,
      tasks,
      trades,
      onDeselectAllTasks,
      onGoToTask,
      onLoadTrades,
      onSelectTaskRequest,
    } = this.props;
    const taskId = tasks[index].id;

    if (trades.length === 0) {
      onLoadTrades();
    }
    onSelectTaskRequest(currentWorkOrder.id, taskId, currentUser).then(
      (response) => {
        if (response.ok) {
          onDeselectAllTasks();
          onGoToTask(this.props.currentWorkOrder.id, taskId);
        }
      }
    );
  };

  render() {
    // Table Headers
    let headers = [];
    if (this.showSelectTask()) {
      headers.push(this.renderSelectAllTasks());
    }
    headers = headers.concat([
      'TASK #',
      'TASK NAME',
      'SUBTASK TYPE',
      'FLOOR',
      'ROOM',
    ]);
    if (this.userIsAdmin) {
      headers.push('CONTRACTOR');
    }
    if (this.userIsContractor) {
      headers.push('TASK AMOUNT');
    }
    headers.push('STATUS');

    return (
      <SimpleTable
        headers={headers}
        rows={this.mapTasks()}
        emptyMessage="No Tasks"
        onRowSelected={this.handleTaskSelection}
        onGenerateLink={this.handleGenerateLink}
      />
    );
  }

  mapTasks() {
    const { quotes, tasks, changeOrderTasks } = this.props;
    if (tasks !== undefined) {
      return tasks.map((element) => {
        let taskAmount = null;
        let quote = null;
        try {
          quote = quotes.find((quote) => quote.task.id === element.id);
          if (
            quote !== undefined &&
            quote.status !== QuotesStatusKeys.WITHDRAWN
          ) {
            let changeOrderAmount = changeOrderTasks
              .filter(
                (cot) =>
                  cot.task_id === element.id &&
                  (cot.status === QuotesStatusKeys.APPROVED ||
                    cot.status === QuotesStatusKeys.ACR)
              )
              .reduce(
                (total, cot) =>
                  total + Math.round(cot.change_order_quote.estimate),
                0
              );
            taskAmount = Math.round(quote.estimate) + changeOrderAmount;
          }
        } catch (e) {
          console.log(e);
        }

        return [
          this.showSelectTask() ? this.renderSelectTask(element, quote) : null,
          element.number,
          element.name || 'TBD',
          element.sub_trade.label,
          element.floor,
          element.room,
          this.userIsAdmin
            ? element.contractor_name !== undefined &&
              element.contractor_name !== null
              ? element.contractor_name
              : 'TBD'
            : null,
          this.userIsContractor ? (
            taskAmount !== null ? (
              <Number value={taskAmount} format="$0,0" />
            ) : (
              'TBD'
            )
          ) : null,
          <SimpleTaskStatus status={element.status} />,
        ].filter((obj) => obj); // to remove null elements
      });
    } else {
      return [];
    }
  }

  renderSelectAllTasks() {
    return (
      <CheckBox
        checked={this.props.selectAllChecked}
        className="check-select-all"
        onChange={this.handleSelectAllCheck}
      />
    );
  }

  renderSelectTask(task, quote) {
    let result = <div />;
    if (
      (this.userIsSpackleAdmin && task.status !== TaskStatusKeys.CLS) ||
      (this.showCreateQuotes() &&
        quote !== null &&
        (quote.status === QuotesStatusKeys.WAITING ||
          quote.status === QuotesStatusKeys.WITHDRAWN))
    ) {
      result = (
        <CheckBox
          className="select-task"
          checked={this.props.selectedTasks[task.id] !== undefined}
          onChange={() => this.handleTaskCheckSelection(task)}
        />
      );
    }
    return result;
  }

  showCreateQuotes() {
    const { currentWorkOrder, quotes } = this.props;
    const correctWOStatus =
      currentWorkOrder.status === WorkOrderStatusKeys.PENDING ||
      currentWorkOrder.status === WorkOrderStatusKeys.PLM ||
      currentWorkOrder.status === WorkOrderStatusKeys.WOA;

    const correctQuotesStatus =
      quotes.filter(
        (quote) =>
          quote.status === QuotesStatusKeys.WAITING ||
          quote.status === QuotesStatusKeys.WITHDRAWN
      ).length > 0;

    return this.userIsContractor && correctWOStatus && correctQuotesStatus;
  }

  showSelectTask() {
    const { currentWorkOrder } = this.props;

    if (this.userIsContractor) {
      // To decide if the task can be selected, dont check quote status
      return (
        currentWorkOrder.status === WorkOrderStatusKeys.PENDING ||
        currentWorkOrder.status === WorkOrderStatusKeys.PLM ||
        currentWorkOrder.status === WorkOrderStatusKeys.WOA
      );
    }

    if (this.userIsSpackleAdmin) {
      return (
        currentWorkOrder.status === WorkOrderStatusKeys.PENDING ||
        currentWorkOrder.status === WorkOrderStatusKeys.PLM
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/admin/work-orders/details/tasks/TasksSimpleTable.js